import { Suspense, lazy } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AuthProvider, AuthTokenSync } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageActionsProvider } from "@/contexts/PageActionsContext";
import { UnsavedChangesProvider } from "@/contexts/UnsavedChangesContext";
import { TopNav } from "@/components/layout";

// Keep eager: Home page (RevenueAnalytics), NotFound, PlaceholderPage, Login
import NotFound from "@/pages/not-found";
import { PlaceholderPage } from "@/pages/placeholder";
import RevenueAnalytics from "@/pages/analytics/revenue";
import LoginPage from "@/pages/login";

// Lazy load: Main data pages
const ClientsPage = lazy(() => import("@/pages/clients"));
const ProjectsPage = lazy(() => import("@/pages/projects"));
const CostsPage = lazy(() => import("@/pages/costs"));
const RevenuesPage = lazy(() => import("@/pages/revenues"));
const SuppliersPage = lazy(() => import("@/pages/suppliers"));

// Lazy load: Analytics (heavy recharts)
const ProjectsAnalytics = lazy(() => import("@/pages/analytics/projects"));
const ClientsAnalytics = lazy(() => import("@/pages/analytics/clients"));

// Lazy load: Import wizards
const ImportClients = lazy(() => import("@/pages/import-clients"));
const ImportRevenues = lazy(() => import("@/pages/import-revenues"));
const ImportProjects = lazy(() => import("@/pages/import-projects"));
const ImportCosts = lazy(() => import("@/pages/import-costs"));
const ImportSuppliers = lazy(() => import("@/pages/import-suppliers"));

// Lazy load: Management & Settings
const ManagementClients = lazy(() => import("@/pages/management/clients"));
const ClientProfile = lazy(() => import("@/pages/management/clients/[nrPorzadkowy]"));
const SettingsPage = lazy(() => import("@/pages/settings"));

function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

/**
 * Protected router - all routes except login require authentication
 */
function ProtectedRouter() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Switch>
        <Route path="/" component={RevenueAnalytics} />
        <Route path="/clients" component={ClientsPage} />
        <Route path="/import-clients" component={ImportClients} />
        <Route path="/import-revenues" component={ImportRevenues} />
        <Route path="/import-projects" component={ImportProjects} />
        <Route path="/import-costs" component={ImportCosts} />
        <Route path="/projects" component={ProjectsPage} />
        <Route path="/costs" component={CostsPage} />
        <Route path="/revenues" component={RevenuesPage} />
        <Route path="/suppliers" component={SuppliersPage} />
        <Route path="/import-suppliers" component={ImportSuppliers} />
        <Route path="/analytics/projects" component={ProjectsAnalytics} />
        <Route path="/analytics/clients" component={ClientsAnalytics} />
        <Route path="/management/clients" component={ManagementClients} />
        <Route path="/management/clients/:nrPorzadkowy" component={ClientProfile} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/settings/:section" component={SettingsPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function VersionBadge() {
  const { data } = useQuery<{ commitId: string }>({
    queryKey: ["/api/version"],
  });

  return (
    <div
      className="fixed bottom-2 right-2 text-xs text-muted-foreground/50 font-mono z-50"
      data-testid="version-badge"
    >
      {data?.commitId ? `v${data.commitId}` : "..."}
    </div>
  );
}

/**
 * Main app layout with sidebar (shown only when authenticated)
 */
function AuthenticatedLayout() {
  const style = {
    "--sidebar-width": "15rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <ProtectedRoute>
      <UnsavedChangesProvider>
        <PageActionsProvider>
          <SidebarProvider style={style as React.CSSProperties}>
            <div className="flex h-screen w-full">
              <AppSidebar />
              <div className="flex flex-col flex-1 overflow-hidden">
                <TopNav />
                <main className="flex-1 overflow-hidden">
                  <ProtectedRouter />
                </main>
              </div>
            </div>
          </SidebarProvider>
        </PageActionsProvider>
        <VersionBadge />
      </UnsavedChangesProvider>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthTokenSync />
        <TooltipProvider>
          <Switch>
            {/* Public route - login page */}
            <Route path="/login" component={LoginPage} />
            {/* All other routes require authentication */}
            <Route>
              <AuthenticatedLayout />
            </Route>
          </Switch>
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
